import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Ruler, Truck, RotateCcw, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import useSeo from "../hooks/useSeo";
import ProductGallery from "../components/product/ProductGallery";
import ColorSelector from "../components/product/ColorSelector";
import SizeSelector from "../components/product/SizeSelector";
import QuantitySelector from "../components/product/QuantitySelector";
import SizeGuideModal from "../components/product/SizeGuideModal";
import Badge from "../components/common/Badge";
import Accordion, { AccordionItem } from "../components/common/Accordion";
import ProductGrid from "../components/product/ProductGrid";
import NotFound from "./NotFound";
import { getProductBySlug, getRelatedProducts } from "../data/products";
import { useStore } from "../context/StoreProvider";
import { formatPrice, discountPercent } from "../lib/utils";

const ProductDetail = () => {
  const { slug } = useParams();
  const product = getProductBySlug(slug);
  const { addToCart, inWishlist, toggleWishlist, pushRecent, recentProducts } = useStore();

  const [color, setColor] = useState(product?.colors?.[0]?.name || "");
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [sizeError, setSizeError] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    if (product) {
      pushRecent(product.id);
      setColor(product.colors[0]?.name || "");
      setSize("");
      setQty(1);
    }
  }, [slug, product, pushRecent]);

  useSeo({
    title: product?.name,
    description: product
      ? `${product.name} — ${product.fit} fit ${product.category.toLowerCase()} from 9'One Store. ${formatPrice(product.price)}. Order via WhatsApp.`
      : undefined,
    path: `/product/${slug}`,
  });

  const related = useMemo(() => getRelatedProducts(product, 4), [product]);
  const recent = useMemo(
    () => recentProducts.filter((p) => p.id !== product?.id).slice(0, 4),
    [recentProducts, product]
  );

  if (!product) return <NotFound />;

  const off = discountPercent(product.price, product.compareAtPrice);

  const handleAdd = () => {
    if (!size) {
      setSizeError("Please select a size");
      toast("Select a size first");
      return;
    }
    setSizeError("");
    addToCart(product, { size, color, quantity: qty });
    toast(`Added to cart — ${product.name}`);
  };

  const liked = inWishlist(product.id);

  return (
    <div className="animate-fade-in">
      <div className="container-9one py-6">
        <Link to="/shop" className="inline-flex items-center gap-2 label-mono text-ink/50 hover:text-ink" data-testid="back-to-shop">
          <ArrowLeft size={14} /> Back to Shop
        </Link>
      </div>

      <div className="container-9one grid gap-10 pb-16 lg:grid-cols-2 lg:gap-16">
        {/* Gallery */}
        <ProductGallery images={product.images} alt={product.name} />

        {/* Details */}
        <div className="lg:py-4">
          <div className="mb-3 flex flex-wrap gap-1.5">
            {product.badges?.map((b) => <Badge key={b} label={b} />)}
          </div>
          <h1 className="font-display text-4xl uppercase leading-none tracking-tight sm:text-5xl" data-testid="product-title">
            {product.name}
          </h1>
          <div className="mt-4 flex items-center gap-3">
            <span className="font-mono text-2xl" data-testid="product-price">{formatPrice(product.price)}</span>
            {product.compareAtPrice && (
              <span className="font-mono text-lg text-ink/40 line-through">
                {formatPrice(product.compareAtPrice)}
              </span>
            )}
            {off > 0 && <span className="font-mono text-sm text-brand-dark">-{off}% OFF</span>}
          </div>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-ink/70">{product.description}</p>

          {/* Color */}
          <div className="mt-8">
            <ColorSelector colors={product.colors} value={color} onChange={setColor} />
          </div>

          {/* Size */}
          <div className="mt-7">
            <div className="mb-3 flex items-center justify-between">
              <span className="label-mono">Size {size ? `· ${size}` : ""}</span>
              <button
                onClick={() => setGuideOpen(true)}
                className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-ink/60 underline hover:text-ink"
                data-testid="open-size-guide"
              >
                <Ruler size={13} /> Size Guide
              </button>
            </div>
            <SizeSelector
              sizes={product.sizes}
              value={size}
              onChange={(s) => {
                setSize(s);
                setSizeError("");
              }}
              error={sizeError}
            />
          </div>

          {/* Qty + actions */}
          <div className="mt-8 flex items-center gap-4">
            <QuantitySelector value={qty} onChange={setQty} />
            <button onClick={handleAdd} className="btn-solid flex-1" data-testid="add-to-cart-btn">
              Add to Cart
            </button>
            <button
              onClick={() => toggleWishlist(product.id)}
              aria-label="Toggle wishlist"
              aria-pressed={liked}
              data-testid="product-wishlist-btn"
              className="flex h-[52px] w-[52px] shrink-0 items-center justify-center border border-ink transition-colors hover:bg-ink hover:text-paper"
            >
              <Heart size={18} className={liked ? "fill-current" : ""} />
            </button>
          </div>

          {/* Info accordions */}
          <div className="mt-10">
            <Accordion>
              <AccordionItem title="Details & Fit" defaultOpen>
                <ul className="space-y-1.5">
                  <li><span className="text-ink/40">Material:</span> {product.material}</li>
                  <li><span className="text-ink/40">Fit:</span> {product.fit}</li>
                  <li><span className="text-ink/40">Category:</span> {product.category}</li>
                </ul>
              </AccordionItem>
              <AccordionItem title="Care Instructions">
                {product.careInstructions}
              </AccordionItem>
              <AccordionItem title="Shipping Information">
                <p className="flex items-start gap-2">
                  <Truck size={16} className="mt-0.5 shrink-0" />
                  <span>
                    Shipping rates and delivery timelines will be confirmed at order
                    via WhatsApp. (Editable placeholder — no rates configured yet.)
                  </span>
                </p>
              </AccordionItem>
              <AccordionItem title="Returns & Exchange">
                <p className="flex items-start gap-2">
                  <RotateCcw size={16} className="mt-0.5 shrink-0" />
                  <span>
                    Return / exchange policy is being finalised. Please contact us on
                    WhatsApp for any queries. (Editable placeholder.)
                  </span>
                </p>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="container-9one border-t border-ink py-16">
          <h2 className="mb-8 font-display text-4xl uppercase tracking-tight">You may also like</h2>
          <ProductGrid products={related} />
        </section>
      )}

      {/* Recently viewed */}
      {recent.length > 0 && (
        <section className="container-9one border-t border-ink py-16" data-testid="recently-viewed">
          <h2 className="mb-8 font-display text-4xl uppercase tracking-tight">Recently Viewed</h2>
          <ProductGrid products={recent} />
        </section>
      )}

      <SizeGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
};

export default ProductDetail;
